import React, { useEffect, useState } from "react";
import { DateRangeBarChart } from "../../Component/Analytics/DateRangeBarChart";
import AnalyticsDataService from "../../services/analytics";
import { DateTime } from "luxon";
import {
  Box,
  Select,
  VStack,
  HStack,
  Text,
  Input,
  SimpleGrid,
} from "@chakra-ui/react";
import { getFYMonths } from "../../helper/helper";
import { platformOptions, countTypeOptions } from "../../Config/graphfilter";
import { MultiSelect } from "react-multi-select-component";

export const EhcRecords = () => {
  const firstDayOfMonth = DateTime.now().startOf("month").toISODate();

  const yesterday = DateTime.now().minus({ days: 1 }).toISODate();

  const [platforms, setPlatforms] = useState<any[]>(["WEB", "APP"]);
  const [startDate, setStartDate] = useState(firstDayOfMonth);
  const [endDate, setEndDate] = useState(yesterday);

  const [countType, setCountType] = useState("daily");

  const [countData, setCountData] = useState<any>([]);
  const [suggestedExpertCountData, setSuggestedExpertCountData] = useState<any>(
    []
  );

  const [monthlyCounts, setMonthlyCounts] = useState<any[]>([]);

  const [viewMode, setViewMode] = useState("currentWeek");
  const [recordType, setRecordType] = useState("ehcSuggestedExpertsCount");

  const fyMonths = getFYMonths();

  const calculateSum = (arr: any[]) => {
    return arr.reduce((sum: any, item: any) => sum + item.ehc, 0);
  };

  const fetchEhcGraphs = () => {
    AnalyticsDataService.getEhcCount(startDate, endDate, platforms)
      .then((res: any) => {
        if (countType === "daily") {
          const data = res.data.map((item: any) => {
            return {
              date: item.date,
              ehc: item.dailyCounts,
            };
          });
          setCountData(data);
        } else {
          const data = res.data.map((item: any) => {
            return {
              date: item.date,
              ehc: item.totalCounts,
            };
          });
          setCountData(data);
        }
      })
      .catch((e: any) => {
        console.log(e);
      });
  };

  const fetchExpertRecords = () => {
    let startDate = "";
    let endDate = "";

    const today = new Date();

    if (viewMode === "currentMonth") {
      const monthDate = DateTime.now();
      startDate = monthDate.startOf("month").toISODate(); // first day
      endDate = monthDate.endOf("month").toISODate();
      console.log(startDate, endDate);
    } else if (viewMode === "currentWeek") {
      const dayOfWeek = today.getDay(); // 0 (Sun) - 6 (Sat)
      const diffToMonday =
        today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      const monday = new Date(today.setDate(diffToMonday));
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);

      startDate = monday.toISOString().slice(0, 10);
      endDate = sunday.toISOString().slice(0, 10);
      // console.log(startDate, endDate);
    } else {
      const monthDate = DateTime.fromFormat(viewMode, "yyyy-MM");
      if (monthDate.isValid) {
        const first = monthDate.startOf("month");
        const last = monthDate.endOf("month");
        startDate = first.toISODate();
        endDate = last.toISODate();

        // console.log(startDate, endDate);
      }
    }

    AnalyticsDataService.getEhcSuggestedExpertCount(startDate, endDate)
      .then((res: any) => {
        const data = res.data.map((item: any) => {
          return {
            count: item.count,
            expertName: item.full_name,
            expertPic: item.photograph,
          };
        });
        setSuggestedExpertCountData(data);
      })
      .catch((e: any) => {
        console.log(e);
      });
  };

  const fetchMonthlyCounts = async () => {
    const results = await Promise.all(
      fyMonths.map(async (m) => {
        try {
          const res = await AnalyticsDataService.getEhcCount(
            m.startDate,
            m.endDate,
            platforms
          );

          const data = res.data.map((item: any) => {
            return {
              date: item.date,
              ehc: item.dailyCounts,
            };
          });

          return { label: m.label, value: calculateSum(data) || 0 };
        } catch (err) {
          console.error("Error fetching count for", m.label, err);
          return { label: m.label, value: 0 };
        }
      })
    );
    setMonthlyCounts(results);
  };

  useEffect(() => {
    fetchEhcGraphs();
  }, [startDate, endDate, countType, platforms]);

  useEffect(() => {
    fetchMonthlyCounts();
  }, [platforms]);

  useEffect(() => {
    fetchExpertRecords();
  }, [viewMode, recordType]);

  return (
    <>
      {/* <Box
        bg="#e98b90"
        color="white"
        fontWeight="bold"
        textAlign="center"
        textColor="black"
        py={4}
        px={6}
      >
        Free Consultation
      </Box> */}

      <Box px={6} py={0} maxW="100%" bg="gray.50">
        <VStack align="start" spacing={0}>
          <Box my={8}>
            <Text fontSize="lg" fontWeight="bold" mb={3}>
              Monthly Counts
            </Text>
            <SimpleGrid columns={{ base: 2, md: 8 }} spacing={2}>
              {monthlyCounts.map((m) => (
                <Box
                  key={m.label}
                  p={2}
                  borderWidth="1px"
                  borderRadius="lg"
                  boxShadow="sm"
                  textAlign="center"
                  bg="white"
                >
                  <Text fontSize="sm" color="gray.500">
                    {m.label}
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color="teal.600">
                    {m.value}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
          <HStack spacing={8} align="center">
            <Text fontSize="lg" fontWeight="semibold">
              From
            </Text>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />

            <Text fontSize="lg" fontWeight="semibold">
              To
            </Text>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              max={DateTime.now().toISODate()}
            />
            <Select
              placeholder="Select Count Type"
              value={countType}
              onChange={(e) => setCountType(e.target.value)}
            >
              {countTypeOptions.map((item: any, index: any) => (
                <option key={index} value={item.value}>
                  {item.label}
                </option>
              ))}
            </Select>

            <MultiSelect
              options={platformOptions}
              labelledBy="Select Platforms"
              value={platformOptions.filter((opt) =>
                platforms.includes(opt.value)
              )}
              onChange={(selected: any) =>
                setPlatforms(selected.map((opt: any) => opt.value))
              }
            />
          </HStack>

          {/* {countType === "daily" && (
            <Text fontSize="md" fontWeight="bold">
              Total EHC: {calculateSum(countData)}
            </Text>
          )} */}

          <Box w="100%" mt={6}>
            <DateRangeBarChart
              data={countData}
              xKey="date"
              barKey="ehc"
              isSessionCount={false}
            />
          </Box>
        </VStack>
      </Box>

      <Box p={6} maxW="100%" bg="gray.50">
        <Box my={8}>
          <Text fontSize="lg" fontWeight="bold" mb={3}>
            Suggested Experts Counts
          </Text>
        </Box>
        <VStack align="start" spacing={4}>
          <HStack spacing={4} align="center">
            <Select
              w="200px"
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
            >
              <option value="currentWeek">Current Week</option>
              {/* <option value="currentMonth">Current Month</option> */}

              <optgroup label="FY 2025-2026">
                {fyMonths.map((m, index) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </optgroup>
            </Select>
          </HStack>

          <Box w="100%" mt={6}>
            {suggestedExpertCountData.length > 0 && (
              <DateRangeBarChart
                data={suggestedExpertCountData}
                xKey="expertName"
                barKey="count"
                isSessionCount={recordType === "sessionsCount"}
                isPeakHoursCount={recordType === "peakHoursCount"}
              />
            )}
          </Box>
        </VStack>
      </Box>
    </>
  );
};
